import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FaCloudUploadAlt, FaDownload, FaSyncAlt, FaUndo } from "react-icons/fa";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { getFilenameFromHeaders, saveBlob } from "../../common/utils/downloadBlob";
import { usePageToast } from "../../hooks/usePageToast";
import { restoreDatabaseBackup, restoreDatabaseFromUpload } from "../../services/backup.service";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

const RESTORE_CONFIRMATION_PHRASE = "RESTAURAR BANCO DE DADOS COMPLETO";

/**
 * Item de backup retornado por GET /api/v1/backups/all
 */
interface BackupItem {
  fileName: string;
  sizeBytes: number;
  sizeHuman: string;
  modifiedAt: string; // ISO string
  database: string;
  extension: ".sql" | ".gz";
}

interface BackupsModalProps {
  open: boolean;
  onClose: () => void;
}

const boxStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(820px, 95vw)",
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
};

const BackupsModal: React.FC<BackupsModalProps> = ({ open, onClose }) => {
  const [items, setItems] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);
  const [restorePhrase, setRestorePhrase] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPhrase, setUploadPhrase] = useState("");
  const [uploadRestoring, setUploadRestoring] = useState(false);

  const containerId = "toaster-backups";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  const loadBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<BackupItem[]>("/backups/all");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItems([]);
      autoError(extractErrorMessage(err) || "Falha ao listar backups.");
    } finally {
      setLoading(false);
    }
  }, [autoError]);

  useEffect(() => {
    if (open) loadBackups();
  }, [open, loadBackups]);

  const handleDownload = async (fileName: string) => {
    setDownloading(fileName);
    try {
      const res = await api.get("/backups/download", {
        params: { file: fileName },
        responseType: "blob",
      });
      const filename = getFilenameFromHeaders(res.headers, fileName);
      saveBlob(res.data, filename);
      autoSuccess(`Download iniciado: ${filename}`);
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao baixar o backup.");
    } finally {
      setDownloading(null);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      autoSuccess("Backup gerado com sucesso.");
      await loadBackups();
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao gerar o backup.");
    } finally {
      setRunning(false);
    }
  };

  const openRestoreConfirm = (fileName: string) => {
    setUploadPanelOpen(false);
    setRestoreTarget(fileName);
    setRestorePhrase("");
  };

  const cancelRestore = () => {
    setRestoreTarget(null);
    setRestorePhrase("");
  };

  const openUploadPanel = () => {
    setRestoreTarget(null);
    setUploadFile(null);
    setUploadPhrase("");
    setUploadPanelOpen(true);
  };

  const cancelUploadRestore = () => {
    setUploadPanelOpen(false);
    setUploadFile(null);
    setUploadPhrase("");
  };

  const handleConfirmUploadRestore = async () => {
    if (!uploadFile) return;
    setUploadRestoring(true);
    try {
      const res = await restoreDatabaseFromUpload(uploadFile, uploadPhrase);
      autoSuccess(
        `Restauração concluída a partir de "${uploadFile.name}". Backup de segurança prévio: ${res.preRestoreBackup ?? "não gerado"}.`
      );
      cancelUploadRestore();
      await loadBackups();
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao restaurar a partir do arquivo enviado.");
    } finally {
      setUploadRestoring(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await restoreDatabaseBackup(restoreTarget, restorePhrase);
      autoSuccess(
        `Restauração concluída. Backup de segurança prévio: ${res.preRestoreBackup ?? "não gerado"}.`
      );
      cancelRestore();
      await loadBackups();
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao restaurar o banco de dados.");
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString("pt-BR");
    } catch {
      return iso;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="h2">
            Backups do banco de dados
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FaSyncAlt />}
              onClick={loadBackups}
              disabled={loading || running}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleRunNow}
              disabled={running}
            >
              {running ? "Gerando..." : "Gerar agora"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<FaCloudUploadAlt />}
              onClick={openUploadPanel}
              disabled={restoring || uploadRestoring}
            >
              Restaurar de arquivo
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflow: "auto", flex: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
            >
              Nenhum backup encontrado.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Arquivo</TableCell>
                    <TableCell>Banco</TableCell>
                    <TableCell align="right">Tamanho</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="center">Baixar</TableCell>
                    <TableCell align="center">Restaurar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.fileName} hover>
                      <TableCell
                        sx={{ wordBreak: "break-all", maxWidth: 320 }}
                      >
                        {item.fileName}
                      </TableCell>
                      <TableCell>{item.database}</TableCell>
                      <TableCell align="right">{item.sizeHuman}</TableCell>
                      <TableCell>{formatDate(item.modifiedAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Baixar este backup">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleDownload(item.fileName)}
                              disabled={downloading === item.fileName}
                            >
                              {downloading === item.fileName ? (
                                <CircularProgress size={18} />
                              ) : (
                                <FaDownload size={16} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Restaurar o banco a partir deste arquivo (substitui todos os dados atuais)">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => openRestoreConfirm(item.fileName)}
                              disabled={restoring}
                            >
                              <FaUndo size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {restoreTarget && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 1,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography color="error" variant="subtitle2">
              Restaurar "{restoreTarget}" substitui TODOS os dados atuais do banco. Um backup de
              segurança será gerado automaticamente antes de restaurar. Para confirmar, digite
              exatamente a frase abaixo:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {RESTORE_CONFIRMATION_PHRASE}
            </Typography>
            <TextField
              size="small"
              value={restorePhrase}
              onChange={(e) => setRestorePhrase(e.target.value)}
              placeholder="Digite a frase de confirmação"
              disabled={restoring}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={cancelRestore} disabled={restoring}>
                Cancelar
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleConfirmRestore}
                disabled={restoring || restorePhrase !== RESTORE_CONFIRMATION_PHRASE}
              >
                {restoring ? "Restaurando..." : "Confirmar restauração"}
              </Button>
            </Box>
          </Box>
        )}

        {uploadPanelOpen && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 1,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography color="error" variant="subtitle2">
              Envie um arquivo .sql ou .sql.gz (ex.: um backup salvo localmente ou de outro
              ambiente) para restaurar o banco a partir dele. Isso substitui TODOS os dados
              atuais. Um backup de segurança será gerado automaticamente antes de restaurar.
            </Typography>
            <Button variant="outlined" component="label" disabled={uploadRestoring}>
              {uploadFile ? uploadFile.name : "Selecionar arquivo .sql / .sql.gz"}
              <input
                type="file"
                accept=".sql,.gz"
                hidden
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {RESTORE_CONFIRMATION_PHRASE}
            </Typography>
            <TextField
              size="small"
              value={uploadPhrase}
              onChange={(e) => setUploadPhrase(e.target.value)}
              placeholder="Digite a frase de confirmação"
              disabled={uploadRestoring}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={cancelUploadRestore} disabled={uploadRestoring}>
                Cancelar
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleConfirmUploadRestore}
                disabled={
                  uploadRestoring || !uploadFile || uploadPhrase !== RESTORE_CONFIRMATION_PHRASE
                }
              >
                {uploadRestoring ? "Restaurando..." : "Confirmar restauração"}
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Fechar</Button>
        </Box>

        <PageToaster containerId={containerId} />
      </Box>
    </Modal>
  );
};

export default BackupsModal;
